/**
 * Thin fetch wrapper for the Django API: session cookies (credentials: include) and the CSRF flow from
 * GET /api/auth/csrf/. Nothing is ever stored in localStorage.
 */

export type Query = Record<string, string | number | boolean | null | undefined>

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: unknown

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }

  /** DRF field errors flattened to one message per field (non-field errors under `_`). */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {}
    const d = this.details
    if (d && typeof d === 'object' && !Array.isArray(d)) {
      for (const [key, value] of Object.entries(d as Record<string, unknown>)) {
        out[key === 'non_field_errors' ? '_' : key] = flatten(value)
      }
    } else if (Array.isArray(d)) {
      out._ = flatten(d)
    }
    return out
  }
}

function flatten(value: unknown): string {
  if (Array.isArray(value)) return value.map(flatten).join(' ')
  if (value && typeof value === 'object') return Object.values(value).map(flatten).join(' ')
  return String(value)
}

export function buildQuery(query?: Query): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    params.append(key, String(value))
  }
  const text = params.toString()
  return text ? `?${text}` : ''
}

let csrfToken: string | null = null
export function resetCsrf() {
  csrfToken = null
}

async function ensureCsrf(force = false): Promise<string> {
  if (csrfToken && !force) return csrfToken
  const response = await fetch('/api/auth/csrf/', { credentials: 'include', method: 'GET', headers: {} })
  const body = (await response.json()) as { csrfToken: string }
  csrfToken = body.csrfToken
  return csrfToken
}

const reachabilityListeners = new Set<(online: boolean) => void>()
export function onReachability(listener: (online: boolean) => void): () => void {
  reachabilityListeners.add(listener)
  return () => reachabilityListeners.delete(listener)
}
/** The server could (true) or could not (false) be reached; a fallback-cache response counts as not reached. */
export function reportReachability(online: boolean) {
  reachabilityListeners.forEach((listener) => listener(online))
}

const unauthorizedListeners = new Set<() => void>()
export function onUnauthorized(listener: () => void): () => void {
  unauthorizedListeners.add(listener)
  return () => unauthorizedListeners.delete(listener)
}

export interface RequestOptions {
  query?: Query
  body?: unknown
  form?: FormData
  signal?: AbortSignal
  /** The session probe (/api/auth/me/) expects 401 and must not bounce the user to the login page. */
  silent401?: boolean
}

const SAFE = new Set(['GET', 'HEAD', 'OPTIONS'])

async function parseError(response: Response): Promise<ApiError> {
  let code = 'http_error'
  let message = `La solicitud falló (${response.status}).`
  let details: unknown
  try {
    const data = (await response.json()) as { error?: { code?: string; message?: string; details?: unknown } }
    if (data.error) {
      code = data.error.code ?? code
      message = data.error.message ?? message
      details = data.error.details
    }
  } catch {
    /* non-JSON body */
  }
  return new ApiError(response.status, code, message, details)
}

async function send<T>(method: string, path: string, options: RequestOptions, retried = false): Promise<T> {
  const headers: Record<string, string> = {}
  let body: BodyInit | undefined
  if (options.form) body = options.form
  else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(options.body)
  }
  if (!SAFE.has(method)) headers['X-CSRFToken'] = await ensureCsrf()

  let response: Response
  try {
    response = await fetch(path + buildQuery(options.query), {
      method,
      credentials: 'include',
      headers,
      body,
      signal: options.signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    reportReachability(false)
    throw new ApiError(0, 'network_error', 'No se puede conectar con el servidor. Revisar la conexión e intentar de nuevo.')
  }

  reportReachability(response.headers.get('x-cached-copy') !== '1')
  if (response.status === 204) return undefined as T
  if (response.ok) {
    const type = response.headers.get('Content-Type') ?? ''
    return (type.includes('json') ? await response.json() : await response.blob()) as T
  }

  const error = await parseError(response)
  if (response.status === 403 && !retried && !SAFE.has(method) && /csrf/i.test(error.message)) {
    await ensureCsrf(true)
    return send<T>(method, path, options, true)
  }
  if (response.status === 401 && !options.silent401) unauthorizedListeners.forEach((listener) => listener())
  throw error
}

export const api = {
  get: <T>(path: string, options: RequestOptions = {}) => send<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options: RequestOptions = {}) => send<T>('POST', path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options: RequestOptions = {}) => send<T>('PUT', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options: RequestOptions = {}) => send<T>('PATCH', path, { ...options, body }),
  delete: <T>(path: string, options: RequestOptions = {}) => send<T>('DELETE', path, options),
  upload: <T>(path: string, form: FormData, options: RequestOptions = {}) => send<T>('POST', path, { ...options, form }),
}
